import React, { useState, useEffect, useMemo } from "react"
import { useLocation } from "react-router-dom"
import { Container, Table, Alert } from "reactstrap"
import { useMutation, useQuery } from "@tanstack/react-query"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"

import { getProducts, getProductsWithPresentations, deleteProduct } from "services/products"
import { getProductsPresentations } from "services/product-presentations"
import { Loading } from "components"
import { CreateModalForm, EditModalForm, ImportModalForm } from "components/products"
import storage from "services/storage"
import { authUserKey } from "config/API"
import ListHeader from "ui-componets/list-header"
import ProductsItemList from "components/products/products-item-list"
import TablePaginationFooter from "components/TablePaginationFooter"
import Shortcuts from "components/Shortcuts"
import "assets/scss/custom/components/table-interactive.scss"

const PAGE_SIZE = 50

const ProductsList = () => {
  document.title = "Lista de productos | Punto de venta"

  const Location = useLocation()

  const [searchTxt, setSearchTxt] = useState(new URLSearchParams(Location.search).get("search") ?? "")
  const [searchTerm, setSearchTerm] = useState(new URLSearchParams(Location.search).get("search") ?? "")
  const [currentPage, setCurrentPage] = useState(1)
  const [modalCreate, setmodalCreate] = useState(false)
  const [modalImport, setmodalImport] = useState(false)
  const [modalEdit, setmodalEdit] = useState(false)
  const [productId, setproductId] = useState(null)
  const [showAlertSuccess, setShowAlertSuccess] = useState(false)
  const [msgSuccess, setMsgSuccess] = useState("")
  const ModalProduct = withReactContent(Swal)
  const styleCenterTable = { textAlign: "center", verticalAlign: "middle" }
  const styleLeftTable = { textAlign: "left", verticalAlign: "middle" }

  // Espera 300ms para hacer la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1)
      setSearchTerm(searchTxt.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTxt])

  const queryFilters = useMemo(
    () => ({
      page: currentPage,
      per_page: PAGE_SIZE,
      ...(searchTerm ? { search: searchTerm } : {}),
      ordering: "name",
    }),
    [currentPage, searchTerm]
  )

  const products = useQuery(
    ["products", queryFilters],
    () => getProductsWithPresentations(queryFilters),
    { keepPreviousData: true }
  )
  const productDeleted = useMutation((id) => deleteProduct(id))

  const permissions = storage.get(authUserKey).user_permissions || []
  const canAdd = permissions.some((e) => e.codename === "add_product")
  const canEdit = permissions.some((e) => e.codename === "change_product")
  const canDelete = permissions.some((e) => e.codename === "delete_product")

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer)
      toast.addEventListener("mouseleave", Swal.resumeTimer)
    },
  })

  const toggleModalCreate = () => {
    setmodalCreate(!modalCreate)
  }

  const toggleModalImport = () => {
    setmodalImport(!modalImport)
  }

  const toggleModalEdit = () => {
    if (modalEdit) {
      setproductId("")
    }
    setmodalEdit(!modalEdit)
  }

  const openEditModal = (productId) => {
    setproductId(productId)
    toggleModalEdit()
  }

  const handleChangeSearch = (event) => {
    setSearchTxt(event.target.value)
  }

  const handleChangePage = (nextPage) => {
    if (nextPage < 1) return
    setCurrentPage(nextPage)
  }

  const openDeleteModal = (row) => {
    Swal.fire({
      title: `Esta seguro que desea desactivar el producto: <b>${row.name}</b>?`,
      text: "No aparecera en ventas nuevas y tambien se desactivaran sus presentaciones. El historial se conserva.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Desactivar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        productDeleted.mutateAsync(row?.id).then(() => {
          products.refetch()
          Swal.fire("Desactivado", `El producto <b>${row.name}</b> ha sido desactivado correctamente`, "success")
        }).catch((error)=>{
          Toast.fire("Error", error?.errors?.[0]?.message || error?.message || "No se pudo desactivar el producto", "error")
        })
      }
    })
  }
  const handleSearch = () => {
    setCurrentPage(1)
    setSearchTerm(searchTxt.trim())
  }

  const handleOnKeyDownSearch = async (event) => {
    if (event.key !== "Enter") {
      return
    }

    event.preventDefault()

    const exactBarCode = searchTxt.trim()
    if (!exactBarCode) {
      handleSearch()
      return
    }

    if (!canEdit) {
      Toast.fire("Sin permisos", "No cuentas con permisos para editar productos", "warning")
      return
    }

    try {
      const response = await getProducts({
        bar_code__iexact: exactBarCode,
        per_page: 1,
        offset: 0,
      })

      const exactProduct = response?.results?.find(
        (product) => String(product?.bar_code || "").trim() === String(exactBarCode).trim()
      )

      if (!exactProduct?.id) {
        const presentationResponse = await getProductsPresentations({
          key__icontains: exactBarCode,
          per_page: 10,
          offset: 0,
        })

        const exactPresentation = presentationResponse?.results?.find(
          (presentation) => String(presentation?.key || "").trim() === String(exactBarCode).trim()
        )

        const parentProductId = exactPresentation?.product?.id || exactPresentation?.product

        if (!parentProductId) {
          Toast.fire("Sin coincidencia", `No se encontró un producto con código de barras: ${exactBarCode}`, "info")
          return
        }

        setSearchTxt(exactBarCode)
        setSearchTerm(exactBarCode)
        setCurrentPage(1)
        openEditModal(parentProductId)
        return
      }

      setSearchTxt(exactBarCode)
      setSearchTerm(exactBarCode)
      setCurrentPage(1)
      openEditModal(exactProduct.id)
    } catch (error) {
      Toast.fire("Error", error?.errors?.[0]?.message || "No se pudo buscar el producto", "error")
    }
  }

  const isLoadingProducts = products.isLoading || productDeleted.isLoading
  const totalCount = products.data?.count || 0

  useEffect(() => {
    if (showAlertSuccess) {
      ModalProduct.fire({
        icon: "success",
        title: msgSuccess,
        didOpen: () => {
          products.refetch()
          setShowAlertSuccess(false)
        },
        confirmButtonText: "Aceptar",
        confirmButtonColor: "green",
      })
    }
  }, [showAlertSuccess])

  const isLoading = isLoadingProducts

  const StyleTableHeaderMoney = {
    position: "sticky",
    background: "white",
    top: "0px",
    zIndex: "3",
    textAlign: "right",
    verticalAlign: "right",
  }

  return !permissions.some((e) => e.codename === "view_product") ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <React.Fragment>
      <div className="page-content">
        {canAdd && (
          <CreateModalForm
            isOpen={modalCreate}
            toggle={toggleModalCreate}
            refetch={products.refetch}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        {canAdd && (
          <ImportModalForm
            isOpen={modalImport}
            toggle={toggleModalImport}
            refetch={products.refetch}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        {canEdit && productId && (
          <EditModalForm
            isOpen={modalEdit}
            toggle={toggleModalEdit}
            productId={productId}
            refetch={products.refetch}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        <Container fluid>
          <Alert color="danger" isOpen={products.isError}>
            {products?.error?.errors?.map((error) => error?.message)}
          </Alert>
          <Alert color="success" isOpen={showAlertSuccess} toggle={() => setShowAlertSuccess(false)}>
            {msgSuccess}
          </Alert>

          <h2>Lista de Productos</h2>
          <div className="bg-white p-3">
            <ListHeader
              canAdd={canAdd}
              handleOnChangeSearch={handleChangeSearch}
              handleOnKeyDownSearch={handleOnKeyDownSearch}
              handleSearch={handleSearch}
              searchTxt={searchTxt}
              toggleModalCreate={toggleModalCreate}
              toggleModalImport={toggleModalImport}
            />
            {!products.error && isLoading ? (
              <div className="my-5 text-center">
                <Loading isLoading={isLoading} size={2} />
              </div>
            ) : products.error && !isLoading ? (
              <div className="my-5 text-center">
                <h1>Error intenta mas tarde</h1>
              </div>
            ) : (
              <div className="table-responsive" style={{ marginTop: "30px" }}>
                <Table className="table-hover-effect table-interactive">
                  <thead>
                    <tr>
                      <th style={styleLeftTable}>Código de barras</th>
                      <th style={styleLeftTable}>Nombre</th>
                      <th style={StyleTableHeaderMoney} className="money-cell">Precio de Lista</th>
                      <th style={StyleTableHeaderMoney} className="money-cell">Precio de Mayoreo</th>
                      <th style={styleCenterTable}>Código de SAT</th>
                      <th style={StyleTableHeaderMoney} className="money-cell">Costo</th>
                      <th style={styleCenterTable}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products?.data?.results?.length ? (
                      products?.data?.results?.map((item) => {
                        return (
                          <ProductsItemList
                            key={item.id}
                            item={item}
                            canDelete={canDelete}
                            canEdit={canEdit}
                            openDeleteModal={openDeleteModal}
                            openEditModal={openEditModal}
                          />
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center">
                          No hay datos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <TablePaginationFooter
                  totalCount={totalCount}
                  currentPage={currentPage}
                  pageSize={PAGE_SIZE}
                  itemLabel="productos"
                  isLoading={isLoading}
                  isFetching={products.isFetching}
                  onChangePage={handleChangePage}
                />
              </div>
            )}
          </div>
        </Container>
      </div>
      <Shortcuts
        toggleModalCreate={toggleModalCreate}
        toggleModalImport={toggleModalImport}
      />
    </React.Fragment>
  )
}

export default ProductsList
